import { Workarea } from "../../../../../domain/job"
import { WorkareaGroup } from "../../../../../domain/workareas/group"
import { centsToDollarsDisplay } from "../../../../displayFormatters"
import { newWorkareaSummary, WorkareaSummary } from "../../../../Items/itemSummaryModel"

export type TotalsCardData = {
  finalPrice?: number
  summaryData?: WorkareaSummary
  group: WorkareaGroup
  selectedWorkarea: Workarea
  alreadyInvoiced?: number
}

export type TotalsCardViewModel = {
  isOverridden: boolean
  overrideButtonText: string
  showResetButton?: boolean
  resetAmount: number | null
  resetButtonText: string
  totalData: {
    totalText: string
    totalPrice: number
    totalHours: number
  },
  perVisitData: {
    perVisitPrice: number
    perVisitHours: number | null
  },
  estimatedPrice: number
  estimatedCost: number
  breakeven: number
  recovery: number
  showRecovery: boolean
  netProfit: number
  netProfitPercentage: number
  minimumCustomizableAmount?: number
  minimumCustomizableAmountMessage: string | null
}

export const defaultTotalsCardViewModel = (): TotalsCardViewModel => ({
  isOverridden: false,
  overrideButtonText: 'Override',
  showResetButton: false,
  resetAmount: null,
  resetButtonText: 'Reset',
  totalData: {
    totalText: 'Total',
    totalPrice: 0,
    totalHours: 0,
  },
  perVisitData: {
    perVisitPrice: 0,
    perVisitHours: null,
  },
  estimatedPrice: 0,
  estimatedCost: 0,
  breakeven: 0,
  recovery: 0,
  showRecovery: false,
  netProfit: 0,
  netProfitPercentage: 0,
  minimumCustomizableAmount: 0,
  minimumCustomizableAmountMessage: null,
})

export const createViewModel = (priceDetails: TotalsCardData): TotalsCardViewModel => {
  const { summaryData, selectedWorkarea, alreadyInvoiced } = priceDetails
  const {
    estimatedHours,
    estimatedPrice: summaryEstimatedPrice,
    estimatedHoursPerOccurrence,
    estimatedCostPlusTax,
    estimatedBreakEven,
    recovery,
    estimatedNetProfit,
    estimatedNetProfitPercentage,
    finalPrice: summaryFinalPrice,
    finalNetProfitPercentage,
    perVisitNetProfitPercentage,
    perVisitPrice,
    isOverridden,
    total,
    isEstimated,
    isPerVisit
  } = summaryData || newWorkareaSummary(selectedWorkarea.id)

  const displayedOccurrences = selectedWorkarea.pricing && isPerVisit ? selectedWorkarea.estimatedOccurrences ?? 1 : 1
  const hours = estimatedHours
  const estimatedPrice = summaryEstimatedPrice
  const invoicedAmount = alreadyInvoiced ?? 0

  const isAtMinimum = summaryFinalPrice === invoicedAmount && invoicedAmount > 0
  const showResetButton = isPerVisit
    ? isOverridden
    : isOverridden && !isAtMinimum
  const resetAmount = isPerVisit ? null : invoicedAmount > estimatedPrice ? invoicedAmount : null

  const totalData = {
    totalText: selectedWorkarea.pricing
      ? `${isEstimated ? 'Estimated ' : ''}Customer Total${isOverridden ? ' with Override' : ''}`
      : `${isEstimated ? 'Estimated ' : ''}${isOverridden ? 'Total with Override' : 'Total'}`,
    totalPrice: isPerVisit && selectedWorkarea.estimatedOccurrences
      ? total * selectedWorkarea.estimatedOccurrences
      : total,
    totalHours: isPerVisit && selectedWorkarea.estimatedOccurrences && estimatedHoursPerOccurrence
      ? selectedWorkarea.estimatedOccurrences * estimatedHoursPerOccurrence
      : hours
  }

  const perVisitData = {
    perVisitPrice: perVisitPrice,
    perVisitHours: isPerVisit ? estimatedHoursPerOccurrence : null
  }

  let netProfitPercentage = (isEstimated ? estimatedNetProfitPercentage : finalNetProfitPercentage)
  if (isPerVisit) {
    netProfitPercentage = perVisitNetProfitPercentage
  }

  return {
    isOverridden,
    overrideButtonText: isPerVisit
      ? 'Override Price Per Visit'
      : 'Override',
    showResetButton,
    resetAmount,
    resetButtonText: isPerVisit
      ? 'Reset'
      : invoicedAmount > estimatedPrice
        ? 'Reset to Invoiced Amount'
        : 'Reset',
    totalData,
    perVisitData,
    estimatedCost: estimatedCostPlusTax * displayedOccurrences,
    estimatedPrice,
    breakeven: estimatedBreakEven * displayedOccurrences,
    recovery: recovery * (isPerVisit ? selectedWorkarea.estimatedOccurrences ?? 1 : 1),
    showRecovery: recovery > 0,
    netProfit: estimatedNetProfit * displayedOccurrences,
    netProfitPercentage,
    minimumCustomizableAmount: isPerVisit
      ? 0
      : Math.max(invoicedAmount, 0),
    minimumCustomizableAmountMessage: isPerVisit
      ? null
      : invoicedAmount > 0
        ? `The new value may not be lower than the already invoiced amount of ${centsToDollarsDisplay(invoicedAmount)}`
        : null
  }
}